import { Stack, Typography, useTheme, Alert } from "@mui/material";
import { FormPropertyComponent, FormPropertyProps } from "../../FormProperty";
import { Property } from "src/api/sure-api/ssq/useGetOneSsqVersion";
import { OlapScoresService } from "src/context/OlapScores/OlapScoresService";
import { usePropertyContext } from "../../context/usePropertyContext";
import { overrideProperty } from "../../context/utils/overrideProperty";
import { isPropertyHidden } from "src/utils/ssq";
import { useQuestionnaireFormContext } from "src/components/QuestionnaireForm/context/useQuestionnaireForm";

export const GroupSimple = (props: FormPropertyProps) => {
    const theme = useTheme();
    const { property, dimensionValue, position } = props;
    const { ssqSsqHeadingSsqProperty, ssqVersionStructureId, post, getValidationError } = usePropertyContext();
    const { ingredientGenericValues } = useQuestionnaireFormContext();

    // Do we really need that ?
    // Or in the context, already processed;
    const children = ssqSsqHeadingSsqProperty.children ?? [];
    const ssqSsqHeadingSsqPropertyChildrenBySsqPropertyId = children.reduce(
        (acc, child) => {
            acc[child.ssqProperty.id] = child;
            return acc;
        },
        {} as Record<number, (typeof children)[number]>,
    );

    const childProperties = (property.children ?? [])
        .filter(
            (property) =>
                !property.hidden && !isPropertyHidden(property.id, post?.hiddenElements ?? [], ssqVersionStructureId),
        )
        .sort((propertyA: Property, propertyB: Property) => (propertyA.position ?? 0) - (propertyB.position ?? 0));

    const subpropertiesDimension = OlapScoresService.getDimension("subproperties", dimensionValue?.dimensions);
    const subpropertiesScoresLength = subpropertiesDimension?.values.length ?? 0;
    const showSubpropertiesScores = subpropertiesScoresLength > 1;

    // Why normalizing in flatMap if just to know if shouldShowScore?
    const scoresByProperties = childProperties.flatMap((property) => {
        const subpropertyDimensionValue = OlapScoresService.getPropertyDimensionValue(
            property.id,
            ssqVersionStructureId,
            subpropertiesDimension,
        );
        const subpropertyScore = OlapScoresService.normalizeNumericalScore(subpropertyDimensionValue?.score.value);

        if (subpropertyScore === null) return [];

        return [subpropertyScore];
    });

    // I feel it maybe be the only line that belongs here
    const shouldShowScore = scoresByProperties.length > 1;

    // IN TRUTH, MAYBE CENTRALIZED ?
    const validationState = getValidationError(property.id, position ?? 0);

    const getValidationStyles = () => {
        if (!validationState.hasValidationMessages) return {};

        const isError = validationState.validationSeverity === "error";
        const color = isError ? theme.custom.red : theme.custom.orange;
        const backgroundColor = isError ? theme.custom.red[50] : theme.custom.orange[50];

        return {
            backgroundColor: backgroundColor,
            border: `1px solid ${color[500]}`,
            borderRadius: "12px",
            padding: "16px",
        };
    };

    return (
        <>
            {validationState.hasValidationMessages && (
                <div style={{ width: "100%" }}>
                    {validationState.validationMessages
                        .filter((msg) => msg.message)
                        .map((message, index) => {
                            const isError = message.severity === "error";
                            const color = isError ? theme.custom.red : theme.custom.orange;
                            const backgroundColor = isError ? theme.custom.red[50] : theme.custom.orange[50];
                            const baseStyles = {
                                width: "100%",
                                backgroundColor: backgroundColor,
                                borderLeft: `4px solid ${color[500]}`,
                                color: color[900],
                                "& .MuiAlert-icon": {
                                    color: color[500],
                                },
                            };

                            return (
                                <Alert
                                    key={index}
                                    severity={message.severity === "error" ? "error" : "warning"}
                                    sx={baseStyles}
                                >
                                    {message.message}
                                </Alert>
                            );
                        })}
                </div>
            )}
            <Stack direction="column" gap="24px" sx={getValidationStyles()}>
                {childProperties.map((_childProperty, index: number) => {
                    // SCORE DRILLDOWN + OVERRIDE
                    const subpropertyScoresValue = OlapScoresService.getPropertyDimensionValue(
                        _childProperty.id,
                        ssqVersionStructureId,
                        subpropertiesDimension,
                    );
                    const childProperty = overrideProperty({
                        originalProperty: _childProperty,
                        potentialOverrides: ssqSsqHeadingSsqPropertyChildrenBySsqPropertyId[_childProperty.id],
                        ingredientGenericValue: ingredientGenericValues[_childProperty.id],
                    });

                    return (
                        <Stack key={index} direction="column" gap="8px">
                            <Typography variant="body-bold" whiteSpace={"wrap"}>
                                {childProperty.title}
                            </Typography>

                            <Stack>
                                <FormPropertyComponent
                                    property={childProperty}
                                    // Position 0 because in a GroupSimple, the elements all have position 0;
                                    position={0}
                                    showSubscore={shouldShowScore}
                                    dimensionValue={showSubpropertiesScores ? subpropertyScoresValue : undefined}
                                />
                            </Stack>
                        </Stack>
                    );
                })}
            </Stack>
        </>
    );
};
